import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';

import { paths } from '@/app/router/paths';
import {
    Button,
    Card,
    CardHeader,
    Field,
    Input,
    MonoLabel,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@/design-system';
import { isSubmitted } from '@/entities/inspection';
import { useSessionStore } from '@/shared/api';
import { useBreakpoint } from '@/shared/lib';
import { ErrorState } from '@/shared/ui';

import { energyCopy, surveyCopy } from './copy';
import { useBackTarget } from './hooks/useBackTarget';
import { useDraftText } from './hooks/useDraftText';
import { useEnergyDraft } from './hooks/useEnergyDraft';
import { addressLine } from './hooks/useSurveyState';
import {
    EVIDENCE_SOURCES,
    HEATING_OPTIONS,
    isEvidenceSource,
    SHELL_PARTS,
    VENTILATION_OPTIONS,
    WATER_OPTIONS,
    type EnergyDraft,
    type ShellEntry,
    type ShellPart,
} from './model/energy';

/**
 * Scherm 7 - opname energielabel NTA 8800 (basismethode).
 *
 * De inspecteur legt vast wat er ter plaatse te zien is. De app berekent geen
 * label en toont er dus ook geen: dat gebeurt in een eigen rekenprogramma en
 * in EP-online. Het draadformaat kent nog geen entiteit voor deze opname; wat
 * hier staat blijft op dit apparaat, en dat staat er ook bij.
 */
export function EnergyPerformancePage() {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    /* Tijdens het meekijken is er bewust geen lokale database: zie `tenantScopeOf`. */
    const impersonating = useSessionStore((state) => state.impersonation !== null);

    if (!inspectionId) {
        return <ErrorState error={new Error('Deze keuring bestaat niet.')} />;
    }

    if (impersonating) {
        return <UnavailableWhileImpersonating />;
    }

    return <EnergyScreen inspectionId={inspectionId} />;
}

/**
 * Wie meekijkt, heeft deze opname niet op zijn apparaat. Een leeg formulier dat
 * niets bewaart zou suggereren van wel.
 */
function UnavailableWhileImpersonating() {
    const navigate = useNavigate();

    return (
        <div className="flex h-full min-h-0 flex-col items-start gap-3 overflow-auto bg-[var(--surface-page)] px-[22px] pt-8 pb-10">
            <h1 className="m-0 font-[family-name:var(--font-display)] text-[22px] leading-[1.25] font-semibold text-[var(--text-primary)]">
                {surveyCopy.load.impersonating}
            </h1>
            <p className="m-0 max-w-[520px] text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                {surveyCopy.load.impersonatingBody}
            </p>
            <Button variant="outline" size="md" onClick={() => void navigate(paths.inspections)}>
                {surveyCopy.load.backToOrders}
            </Button>
        </div>
    );
}

function EnergyScreen({ inspectionId }: { inspectionId: string }) {
    const navigate = useNavigate();
    const { level } = useBreakpoint();
    const { inspection, order, draft, update, updateShell } = useEnergyDraft(inspectionId);
    const back = useBackTarget(order?.uuid ?? null);
    const readOnly = isSubmitted(inspection);

    const addressLabel = addressLine(order) || energyCopy.header.unknownAddress;

    const commit = (patch: Partial<Omit<EnergyDraft, 'schil'>>) => {
        if (!readOnly) {
            void update(patch);
        }
    };

    const commitShell = (part: ShellPart, patch: Partial<ShellEntry>) => {
        if (!readOnly) {
            void updateShell(part, patch);
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-col bg-[var(--surface-page)]">
            {/* Net als de opname een werkplek zonder de kop van de keuring: de knop links
                zegt waar je terug naartoe gaat. */}
            <header className="flex flex-wrap items-center justify-between gap-[18px] gap-y-3 bg-[var(--ink-800)] px-[22px] py-[14px]">
                <div className="flex min-w-0 items-center gap-4">
                    <Button
                        variant="onDark"
                        size="md"
                        icon="back"
                        aria-label={back.label}
                        onClick={() => void navigate(back.to)}
                    >
                        {back.short}
                    </Button>
                    <div className="min-w-0">
                        <MonoLabel size={10} tone="onDarkMuted" className="tracking-[0.1em]">
                            {energyCopy.header.eyebrow(order?.number ?? inspectionId)}
                        </MonoLabel>
                        <div className="truncate font-[family-name:var(--font-display)] text-[18px] font-semibold tracking-[-0.01em] text-[var(--text-on-dark)]">
                            {addressLabel}
                        </div>
                    </div>
                </div>

                <span className="rounded-[20px] border border-[var(--warn-on-dark-border)] bg-[var(--warn-on-dark-surface)] px-3 py-2 text-[12px] text-[var(--warn-field)]">
                    {energyCopy.header.localOnly}
                </span>
            </header>

            <div className="flex-1 overflow-auto px-6 pt-5 pb-9">
                <div
                    className="mx-auto flex flex-col gap-[14px]"
                    style={{ maxWidth: level === 'narrow' ? undefined : 960 }}
                >
                    <Card variant="warn" padding="md" role="note">
                        <p className="m-0 text-[14px] leading-[1.4] font-medium text-[var(--warn-text)]">
                            {energyCopy.notice.title}
                        </p>
                        <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                            {readOnly ? energyCopy.locked : energyCopy.notice.body}
                        </p>
                    </Card>

                    <Card padding="lg">
                        <SectionTitle>{energyCopy.general.title}</SectionTitle>
                        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
                            <DraftInput
                                mono
                                inputMode="numeric"
                                label={energyCopy.general.year}
                                value={draft.bouwjaar}
                                readOnly={readOnly}
                                onCommit={(bouwjaar) => commit({ bouwjaar })}
                            />
                            <DraftInput
                                mono
                                label={energyCopy.general.usableArea}
                                value={draft.gebruiksoppervlak}
                                readOnly={readOnly}
                                onCommit={(gebruiksoppervlak) => commit({ gebruiksoppervlak })}
                            />
                            <DraftInput
                                mono
                                label={energyCopy.general.lossArea}
                                value={draft.verliesoppervlak}
                                readOnly={readOnly}
                                onCommit={(verliesoppervlak) => commit({ verliesoppervlak })}
                            />
                        </div>
                    </Card>

                    <Card clip>
                        <CardHeader
                            className="px-[18px]"
                            title={
                                /* Een echte kop, met de typografie van CardHeader. */
                                <h2 style={{ font: 'inherit', margin: 0 }}>
                                    {energyCopy.shell.title}
                                </h2>
                            }
                            action={
                                <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                    {energyCopy.shell.description}
                                </span>
                            }
                        />
                        <Table
                            columns="minmax(96px, 0.8fr) minmax(160px, 1.4fr) minmax(110px, 0.9fr) minmax(140px, 1fr)"
                            minWidth={600}
                            label={energyCopy.shell.title}
                        >
                            <TableHead>
                                <TableHeaderCell>{energyCopy.shell.columns.part}</TableHeaderCell>
                                <TableHeaderCell>
                                    {energyCopy.shell.columns.construction}
                                </TableHeaderCell>
                                <TableHeaderCell>{energyCopy.shell.columns.value}</TableHeaderCell>
                                <TableHeaderCell>{energyCopy.shell.columns.source}</TableHeaderCell>
                            </TableHead>
                            <TableBody>
                                {SHELL_PARTS.map((part) => {
                                    const partLabel = energyCopy.shell.parts[part];
                                    const entry = draft.schil[part];

                                    return (
                                        <TableRow key={part}>
                                            <TableCell emphasis>{partLabel}</TableCell>
                                            <TableCell>
                                                <CellInput
                                                    label={energyCopy.shell.field(
                                                        partLabel,
                                                        energyCopy.shell.columns.construction,
                                                    )}
                                                    value={entry.construction}
                                                    readOnly={readOnly}
                                                    onCommit={(construction) =>
                                                        commitShell(part, { construction })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CellInput
                                                    mono
                                                    label={energyCopy.shell.field(
                                                        partLabel,
                                                        energyCopy.shell.columns.value,
                                                    )}
                                                    value={entry.value}
                                                    readOnly={readOnly}
                                                    onCommit={(value) =>
                                                        commitShell(part, { value })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    aria-label={energyCopy.shell.field(
                                                        partLabel,
                                                        energyCopy.shell.columns.source,
                                                    )}
                                                    value={entry.source}
                                                    disabled={readOnly}
                                                    onChange={(event) =>
                                                        commitShell(part, {
                                                            source: isEvidenceSource(
                                                                event.target.value,
                                                            )
                                                                ? event.target.value
                                                                : '',
                                                        })
                                                    }
                                                >
                                                    <option value="">{energyCopy.noChoice}</option>
                                                    {EVIDENCE_SOURCES.map((source) => (
                                                        <option key={source} value={source}>
                                                            {source}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Card>

                    <Card padding="lg">
                        <SectionTitle>{energyCopy.installations.title}</SectionTitle>
                        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                            <ChoiceField
                                label={energyCopy.installations.heating}
                                value={draft.verwarming}
                                options={HEATING_OPTIONS}
                                readOnly={readOnly}
                                onChange={(verwarming) => commit({ verwarming })}
                            />
                            <ChoiceField
                                label={energyCopy.installations.ventilation}
                                value={draft.ventilatie}
                                options={VENTILATION_OPTIONS}
                                readOnly={readOnly}
                                onChange={(ventilatie) => commit({ ventilatie })}
                            />
                            <DraftInput
                                label={energyCopy.installations.solar}
                                value={draft.zonnepanelen}
                                readOnly={readOnly}
                                onCommit={(zonnepanelen) => commit({ zonnepanelen })}
                            />
                            <ChoiceField
                                label={energyCopy.installations.water}
                                value={draft.tapwater}
                                options={WATER_OPTIONS}
                                readOnly={readOnly}
                                onChange={(tapwater) => commit({ tapwater })}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}

/** Tekstveld dat pas bij `blur` schrijft: één wijziging per bewerking. */
function DraftInput({
    label,
    value,
    onCommit,
    readOnly,
    mono = false,
    inputMode,
}: {
    label: string;
    value: string;
    onCommit: (next: string) => void;
    readOnly: boolean;
    mono?: boolean;
    inputMode?: 'numeric' | 'text';
}) {
    const [text, setText] = useDraftText(value);

    return (
        <Field label={label}>
            <Input
                mono={mono}
                inputMode={inputMode}
                value={text}
                readOnly={readOnly}
                onChange={(event) => setText(event.target.value)}
                onBlur={() => {
                    if (text !== value) {
                        onCommit(text);
                    }
                }}
            />
        </Field>
    );
}

/** Hetzelfde, maar in een tabelcel: het label staat in de kop, dus alleen voor de schermlezer. */
function CellInput({
    label,
    value,
    onCommit,
    readOnly,
    mono = false,
}: {
    label: string;
    value: string;
    onCommit: (next: string) => void;
    readOnly: boolean;
    mono?: boolean;
}) {
    const [text, setText] = useDraftText(value);

    return (
        <Input
            aria-label={label}
            mono={mono}
            value={text}
            readOnly={readOnly}
            onChange={(event) => setText(event.target.value)}
            onBlur={() => {
                if (text !== value) {
                    onCommit(text);
                }
            }}
        />
    );
}

function ChoiceField({
    label,
    value,
    options,
    readOnly,
    onChange,
}: {
    label: string;
    value: string;
    options: readonly string[];
    readOnly: boolean;
    onChange: (next: string) => void;
}) {
    return (
        <Field label={label}>
            <Select
                value={value}
                disabled={readOnly}
                onChange={(event) => onChange(event.target.value)}
            >
                <option value="">{energyCopy.noChoice}</option>
                {options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </Select>
        </Field>
    );
}

function SectionTitle({ children }: { children: ReactNode }) {
    return (
        <h2 className="m-0 mb-[14px] font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
            {children}
        </h2>
    );
}
